import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PLAYBACK_FACTORS, PlaybackFactor, RoutePlayback } from "../hooks/useRoutePlayback";

interface Props {
  playback: RoutePlayback;
  duration: number;
  /** The clock time of the point the film is at, already formatted. */
  currentTimeLabel?: string;
}

/** Film time as h:mm:ss, so a long trip reads at a glance. */
function formatFilmTime(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function ControlButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip title={label}>
      {/* The span keeps the tooltip working on a disabled button. */}
      <span>
        <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** Play the selected trips as a film: forwards, backwards, at a chosen multiple of real time. */
export function RoutePlaybackControls({ playback, duration, currentTimeLabel }: Props) {
  const { t } = useTranslation();
  const { time, direction, factor } = playback;
  const position = time ?? 0;
  const active = time !== null;

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        right: 16,
        // Clear of Leaflet's attribution line in the corner.
        bottom: 28,
        zIndex: 1200,
        width: 420,
        maxWidth: "calc(100% - 32px)",
        px: 1.5,
        py: 0.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <ControlButton label={t("route.playback.toStart")} onClick={() => playback.seek(0)} disabled={!active}>
          <SkipPreviousIcon fontSize="small" />
        </ControlButton>
        <ControlButton
          label={t("route.playback.playBackward")}
          onClick={() => playback.play(-1)}
          disabled={direction === -1}
        >
          <PlayArrowIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
        </ControlButton>
        <ControlButton label={t("route.playback.pause")} onClick={playback.pause} disabled={direction === 0}>
          <PauseIcon fontSize="small" />
        </ControlButton>
        <ControlButton
          label={t("route.playback.playForward")}
          onClick={() => playback.play(1)}
          disabled={direction === 1}
        >
          <PlayArrowIcon fontSize="small" />
        </ControlButton>
        <ControlButton label={t("route.playback.toEnd")} onClick={() => playback.seek(duration)} disabled={!active}>
          <SkipNextIcon fontSize="small" />
        </ControlButton>

        <Typography variant="caption" sx={{ flex: 1, textAlign: "center", whiteSpace: "nowrap" }}>
          {active ? (currentTimeLabel ?? "") : t("route.playback.hint")}
        </Typography>

        <Tooltip title={t("route.playback.speed")}>
          <Select
            size="small"
            variant="standard"
            disableUnderline
            value={factor}
            onChange={event => playback.setFactor(Number(event.target.value) as PlaybackFactor)}
            inputProps={{ "aria-label": t("route.playback.speed") }}
            sx={{ fontSize: 13, mx: 0.5 }}
          >
            {PLAYBACK_FACTORS.map(value => (
              <MenuItem key={value} value={value} dense>
                {t("route.playback.speedValue", { value })}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
        <ControlButton label={t("route.playback.stop")} onClick={playback.stop} disabled={!active}>
          <CloseIcon fontSize="small" />
        </ControlButton>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5 }}>
        <Slider
          size="small"
          min={0}
          max={Math.max(duration, 1)}
          step={1000}
          value={position}
          onChange={(_, value) => playback.seek(value as number)}
          aria-label={t("route.playback.position")}
          disabled={duration <= 0}
        />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {formatFilmTime(position)} / {formatFilmTime(duration)}
        </Typography>
      </Box>
    </Paper>
  );
}
